import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import ProtectedRoute from "@/features/auth/ProtectedRoute";
import RouteErrorBoundary from "@/components/errors/RouteErrorBoundary";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const UnauthorizedPage = lazy(() => import("@/features/auth/UnauthorizedPage"));
const NotFoundPage = lazy(() => import("@/features/NotFoundPage"));

const OverviewPage = lazy(() => import("@/features/overview"));
const RevenuePage = lazy(() => import("@/features/revenue"));
const RevenueRaceLayout = lazy(() => import("@/features/revenue-race/RevenueRaceLayout"));
const RevenueRaceOverviewPage = lazy(() => import("@/features/revenue-race/overview"));
const WeeklyRacePage = lazy(() => import("@/features/revenue-race/weekly"));
const MonthlyRecognitionPage = lazy(() => import("@/features/revenue-race/monthly"));
const NetMrrPerformancePage = lazy(() => import("@/features/revenue-race/net-mrr"));
const RevenueRaceOpportunitiesPage = lazy(() => import("@/features/revenue-race/opportunities"));
const PlacementsPage = lazy(() => import("@/features/revenue-race/placements"));
const ChurnAttritionPage = lazy(() => import("@/features/revenue-race/churn"));
const TeamsDealOwnersPage = lazy(() => import("@/features/revenue-race/teams"));
const WinnersArchivePage = lazy(() => import("@/features/revenue-race/winners"));
const RevenueRaceReportsPage = lazy(() => import("@/features/revenue-race/reports"));
const DataImportSyncPage = lazy(() => import("@/features/revenue-race/data-import"));
const RevenueRaceAdminSettingsPage = lazy(() => import("@/features/revenue-race/admin"));
const PresentationModePage = lazy(() => import("@/features/revenue-race/presentation"));
const SuperBonusLayout = lazy(() => import("@/features/super-bonus/SuperBonusLayout"));
const SuperBonusOverviewPage = lazy(() => import("@/features/super-bonus/overview"));
const SuperBonusEntryPage = lazy(() => import("@/features/super-bonus/entry"));
const SuperBonusAdminPage = lazy(() => import("@/features/super-bonus/admin"));
const ClientsPage = lazy(() => import("@/features/clients"));
const ClientDetailPage = lazy(() => import("@/features/clients/ClientDetailPage"));
const OperationsPage = lazy(() => import("@/features/operations"));
const PeoplePage = lazy(() => import("@/features/people"));
const TalentPage = lazy(() => import("@/features/talent"));
const FinancePage = lazy(() => import("@/features/finance"));
const DepartmentsPage = lazy(() => import("@/features/departments"));
const ProjectsPage = lazy(() => import("@/features/projects"));
const AIInsightsPage = lazy(() => import("@/features/ai-insights"));
const AlertsPage = lazy(() => import("@/features/alerts"));
const InboxPage = lazy(() => import("@/features/inbox"));
const ReportsPage = lazy(() => import("@/features/reports"));
const SettingsPage = lazy(() => import("@/features/settings"));

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/unauthorized", element: <UnauthorizedPage /> },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "revenue-race/presentation", element: <PresentationModePage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <OverviewPage /> },
          {
            path: "revenue-race",
            element: <RevenueRaceLayout />,
            children: [
              { index: true, element: <RevenueRaceOverviewPage /> },
              { path: "weekly", element: <WeeklyRacePage /> },
              { path: "monthly", element: <MonthlyRecognitionPage /> },
              { path: "net-mrr", element: <NetMrrPerformancePage /> },
              { path: "opportunities", element: <RevenueRaceOpportunitiesPage /> },
              { path: "placements", element: <PlacementsPage /> },
              { path: "churn", element: <ChurnAttritionPage /> },
              { path: "teams", element: <TeamsDealOwnersPage /> },
              { path: "winners", element: <WinnersArchivePage /> },
              { path: "reports", element: <RevenueRaceReportsPage /> },
              { path: "data-import", element: <DataImportSyncPage /> },
              { path: "admin", element: <RevenueRaceAdminSettingsPage /> },
            ],
          },
          {
            path: "super-bonus",
            element: <SuperBonusLayout />,
            children: [
              { index: true, element: <SuperBonusOverviewPage /> },
              { path: "entry", element: <SuperBonusEntryPage /> },
              { path: "admin", element: <SuperBonusAdminPage /> },
            ],
          },
          { path: "revenue", element: <RevenuePage /> },
          { path: "clients", element: <ClientsPage /> },
          { path: "clients/:clientId", element: <ClientDetailPage /> },
          { path: "operations", element: <OperationsPage /> },
          { path: "people", element: <PeoplePage /> },
          { path: "talent", element: <TalentPage /> },
          { path: "finance", element: <FinancePage /> },
          { path: "departments", element: <DepartmentsPage /> },
          { path: "projects", element: <ProjectsPage /> },
          { path: "ai-insights", element: <AIInsightsPage /> },
          { path: "alerts", element: <AlertsPage /> },
          { path: "inbox", element: <InboxPage /> },
          { path: "reports", element: <ReportsPage /> },
          { path: "settings", element: <SettingsPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
